import { User } from '@firebase/auth';
import { HubConnection } from '@microsoft/signalr';
import { createContext, useContext } from 'react';

import { AlertMessageType } from '../../elements/alert-message/alert-message';
import {
  AuthStatus,
  IAuthenticationStatus,
  IPasswordStatus,
  IPinCodeStatus,
  IVerification,
} from '../../firebase/authentication';
import { BaseSignalRMessage, SignalRFilter } from '../../hooks/use-signalr';
import {
  CurrentHealthProfessionalItemFragment,
  HealthProviderItemDataFragment,
} from '../types/api/graphql';

export type AppAlertMessage = {
  id?: string;
  type: AlertMessageType;
  message: JSX.Element | string;
};

type AppContextState = {
  loading: boolean;
  token: string | null;
  authUser: User | null;
  lock: () => Promise<void>;
  lockAll: () => Promise<void>;
  // setIdle: (path?: string) => void;
  signIn: (email: string, password: string) => Promise<IAuthenticationStatus>;
  signOut: () => void;
  verifySMSCode: (
    code: string,
    verification?: IVerification
  ) => Promise<IAuthenticationStatus>;
  checkOOBCode: (oobCode: string) => Promise<boolean>;
  checkPinCode: (code: string) => Promise<IAuthenticationStatus>;
  setPinCode: (
    pinCode: string,
    currentPinCode?: string,
    reloadUser?: boolean
  ) => Promise<IPinCodeStatus>;
  forgotPassword: (email: string) => Promise<IPasswordStatus>;
  forgotPinCode: () => Promise<IPinCodeStatus>;
  recoverPassword: (
    resetCode: string,
    password: string,
    confirmPasssword: string
  ) => Promise<IPasswordStatus>;
  verifyWithPassword: (
    currentPassword: string
  ) => Promise<IAuthenticationStatus>;
  setPassword: (password: string) => Promise<IPasswordStatus>;
  status: IAuthenticationStatus | null;
  validating: boolean;
  initialized: boolean;
  timeout: number;
  user: CurrentHealthProfessionalItemFragment | null;
  reloadUser: () => void;
  healthProviderId: string | null | undefined;
  healthProviders: HealthProviderItemDataFragment[];
  updateHealthProviderId: (healthProviderId: string) => void;
  alertMessages: AppAlertMessage[];
  signalRMessage: BaseSignalRMessage | null;
  addFilter: (filter: SignalRFilter) => void;
  removeFilter: (key: string) => void;
  clearFilters: () => void;
  connection: HubConnection | null;
  showAlertMessage: (value: AppAlertMessage) => void;
  hideAlertMessage: (value: string) => void;  
};

export const AppContextProps = {
  loading: false,
  token: null,
  authUser: null,
  lock: () => Promise.resolve(),
  lockAll: () => Promise.resolve(),
  // setIdle: () => Promise.resolve(),
  signIn: async () => ({ status: AuthStatus.UNAUTHORIZED }),
  signOut: () => null,
  verifySMSCode: async () => ({ status: AuthStatus.UNAUTHORIZED }),
  checkOOBCode: async () => false,
  checkPinCode: async () => ({ status: AuthStatus.UNAUTHORIZED }),
  setPinCode: async () => ({ success: false }),
  forgotPassword: async () => ({ success: false }),
  recoverPassword: async () => ({ success: false }),
  verifyWithPassword: async () => ({ status: AuthStatus.UNAUTHORIZED }),
  setPassword: async () => ({ success: false }),
  forgotPinCode: async () => ({ success: false }),
  status: null,
  validating: false,
  initialized: false,
  timeout: 0,
  user: null,
  reloadUser: async () => null,
  healthProviderId: null,
  healthProviders: [],
  updateHealthProviderId: () => null,
  alertMessages: [],
  signalRMessage: null,
  addFilter: () => null,
  removeFilter: () => null,
  clearFilters: () => null,
  connection: null,
  showAlertMessage: () => null,
  hideAlertMessage: () => null,
};

export const AppContext = createContext<AppContextState>(AppContextProps);

export const useAppContext = () => useContext(AppContext);
